import * as THREE from 'three'
import type { VisualParams } from '../../types'
import type { SharedUniforms } from '../AudioUniforms'
import { PRIMITIVE_TEXTURE_GLSL } from '../AudioUniforms'
import { BasePrimitive } from './Primitive'
import { hsv2rgb, POINT_SPRITE_GLSL } from '../shaderChunks'

/**
 * Oscilloscope: the actual waveform (time domain) as a bright line that
 * vibrates with the audio. Drawn as a continuous line + a halo of points so it
 * has visible thickness.
 */
export class Waveform extends BasePrimitive {
  object3D: THREE.Group
  private line: THREE.Line
  private points: THREE.Points
  private lineMat: THREE.ShaderMaterial
  private pointMat: THREE.ShaderMaterial

  constructor(uniforms: SharedUniforms) {
    super(uniforms)
    const shared = uniforms as unknown as Record<string, THREE.IUniform>

    const vertexShader = /* glsl */ `
      attribute float aU;
      uniform float uTime, uLevel, uBeat, uReactivity, uTreble, uPixelRatio, uPointScale;
      uniform vec3 uSeed;
      uniform float uSeedPulse;
      uniform sampler2D uWave;
      varying float vAmp;
      void main() {
        float w = texture2D(uWave, vec2(aU, 0.5)).r;   // 0..1, center 0.5
        float s = (w - 0.5) * 2.0;                     // -1..1
        float amp = (1.4 + uLevel * 1.5 + uBeat) * uReactivity;
        vAmp = abs(s) * amp;
        vec3 pos = position;
        // Seed-driven beat punch only (uBeat decays to 0 between beats, so the base
        // waveform stays original); the seed sets the standing-wave pattern.
        pos.y = s * amp + sin(aU * 15.0 + uSeed.y) * uSeedPulse * 0.6 * uReactivity;
        pos.z += sin(aU * 40.0 + uTime * 2.0) * 0.04;  // slight 3D liveliness
        gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
        gl_PointSize = (4.0 + vAmp * 5.0 + uBeat * 3.0) * uPixelRatio * uPointScale;
      }
    `
    const makeFrag = (isPoint: boolean) => /* glsl */ `
      precision highp float;
      ${PRIMITIVE_TEXTURE_GLSL}
      uniform float uHue, uTreble, uLevel;
      varying float vAmp;
      ${hsv2rgb}
      ${isPoint ? POINT_SPRITE_GLSL : ''}
      void main() {
        ${isPoint ? 'float core = pointMask(); if (core < 0.01) discard;' : 'float core = 1.0;'}
        float hue = fract(uHue + vAmp * 0.15 + uTreble * 0.1);
        vec3 col = hsv2rgb(vec3(hue, 0.7, 0.7 + vAmp * 0.3));
        float alpha = ${isPoint ? '(uPointSolid > 0.5 ? core : core * 0.5)' : '0.9'};
        gl_FragColor = vec4(applyPrimitiveTexture(col), alpha);
      }
    `

    this.lineMat = new THREE.ShaderMaterial({
      uniforms: shared,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      vertexShader,
      fragmentShader: makeFrag(false),
    })
    this.pointMat = new THREE.ShaderMaterial({
      uniforms: shared,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      vertexShader,
      fragmentShader: makeFrag(true),
    })

    this.line = new THREE.Line(new THREE.BufferGeometry(), this.lineMat)
    this.points = new THREE.Points(new THREE.BufferGeometry(), this.pointMat)
    this.line.frustumCulled = false
    this.points.frustumCulled = false
    this.object3D = new THREE.Group()
    this.object3D.add(this.line)
    this.object3D.add(this.points)
    this.rebuild({ density: 1 } as VisualParams)
  }

  update(params: VisualParams): void {
    // Solid affects the point halo only; the line stays an additive glow.
    const solid = params.pointSolid === true
    this.pointMat.blending = solid ? THREE.NormalBlending : THREE.AdditiveBlending
    this.pointMat.depthWrite = solid
  }

  rebuild(params: VisualParams): void {
    const n = Math.floor(512 * (params.density ?? 1))
    const width = 9
    const positions = new Float32Array(n * 3)
    const us = new Float32Array(n)
    for (let i = 0; i < n; i++) {
      const u = i / (n - 1)
      positions[i * 3 + 0] = (u - 0.5) * width
      positions[i * 3 + 1] = 0
      positions[i * 3 + 2] = 0
      us[i] = u
    }
    for (const geo of [this.line.geometry, this.points.geometry]) {
      geo.setAttribute('position', new THREE.BufferAttribute(positions.slice(), 3))
      geo.setAttribute('aU', new THREE.BufferAttribute(us.slice(), 1))
      geo.computeBoundingSphere()
    }
  }
}
